import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { DossierSummaryResponseDto } from 'lib_api/lib/api/gen/api';

import { baseOptionTypeArrayToStringArray } from 'components/BaseForm/utils';
import { Table } from 'components/WrappedComponents';
import {
  ColumnProps,
  FetchDataFunction,
} from 'components/WrappedComponents/Table/types';
import { useAFFilterContext } from 'hooks/AFFilterStoreContext';
import { useSearchDossierSummaries } from 'hooks/dossiers/useSearchDossierSummaries';
import { usePaginationContext } from 'hooks/PaginationStoreContext';
import { useSortContext } from 'hooks/SortStoreContext';
import { useUserStore } from 'hooks/UserStoreContext';

import { FilterDashboardAutoriteFourriere } from '../DashboardHeader/Filters/types';
import { AutoriteFourriereSortOptions } from '../types';

import { buildColumns, buildHeader } from './utils';

function DashboardTable(): React.ReactElement {
  const { pathname } = useLocation();
  const user = useUserStore();
  const [sort, setSort] = useSortContext();
  const [filter, setFilter, afActionsRequises] = useAFFilterContext();
  const searchDossierSummaries = useSearchDossierSummaries();
  const [pagination, setPagination] = usePaginationContext('paginationDossier');

  /**
   * Fetch dossiers from backend based on search parameters and convert result
   */
  const fetchData: FetchDataFunction<
    DossierSummaryResponseDto,
    FilterDashboardAutoriteFourriere,
    AutoriteFourriereSortOptions
  > = useMemo(
    () => (pagination, sorters, filter) => {
      return searchDossierSummaries(pagination, sorters, {
        communeEnlevementList: baseOptionTypeArrayToStringArray(filter.commune),
        classementList: filter.classement,
        dateRangeBonEnlevement: filter.dateRangeBonEnlevement,
        dateRangeMiseEnFourriere: filter.dateRangeMiseEnFourriere,
        dateRangeSortieDefinitive: filter.dateRangeSortieDefinitive,
        dateRangeOrdreDestruction: filter.dateRangeOrdreDestruction,
        idNatureBrancheList: filter.natureMiseEnFourriere,
        numeroRecherche: filter.search,
        idFourriereList: baseOptionTypeArrayToStringArray(filter.fourriere),
        idAutoriteFourriere: user.idCorrelationAutoriteFourriere || undefined,
        statutDossierEnumList: filter.statut,
        includeDossiersSortis: filter.includeDossiersSortis,
        actionsRequises: filter.actionsRequises,
        statutsFacturation: filter.statutsTraitementFacturation,
        idMotifSortieList: filter.motifDeSortie,
        includeDossiersAnonymises: filter.includeDossiersAnonymises,
        isDetruitApresSortie: filter.includeOnlyNotDetruitApresSorti
          ? false
          : undefined,
      });
    },
    [searchDossierSummaries, user.idCorrelationAutoriteFourriere],
  );

  const columns: ColumnProps<
    DossierSummaryResponseDto,
    AutoriteFourriereSortOptions
  >[] = useMemo(
    () => buildColumns(pathname, afActionsRequises),
    [pathname, afActionsRequises],
  );

  return (
    <div className="table-container">
      <Table
        columns={columns}
        header={buildHeader}
        fetchData={fetchData}
        buildItemKey={(item): string => item.id}
        pageSizeOptions={[10, 20, 50]}
        pagination={pagination}
        setPagination={setPagination}
        filter={filter}
        setFilter={setFilter}
        sort={sort}
        setSort={setSort}
      />
    </div>
  );
}

export default DashboardTable;
